import { ArrowLinkText } from '@/components/common/ArrowLinkText';
import Callout from '@/components/common/Callout';
import { FlexRow } from '@/components/common/Flex';
import { QueryResult } from '@apollo/client';
import { Divider, FlexProps, VStack } from '@chakra-ui/react';
import React, { useState } from 'react';
import { Skeletons } from './Skeletons';

interface InfiniteListFooterProps<T extends object> extends FlexProps {
    items: T[];
    renderItem: (item: T) => React.ReactNode;
    queryResult: Pick<QueryResult<any, any>, 'loading' | 'fetchMore'>;
    endCursor: string | null | undefined;
    hasNextPage: boolean;
    emptyState: string;
    onSeeLess?: () => void;
    itemsPerPage: number;
}

export const InfiniteList = <T extends object>({
    items,
    renderItem,
    queryResult,
    endCursor,
    hasNextPage,
    emptyState,
    itemsPerPage,
    onSeeLess,
    ...props
}: InfiniteListFooterProps<T>) => {
    const [hiddenItemsCount, setHiddenItemsCount] = useState(0);
    const { fetchMore, loading } = queryResult;

    if (loading) return <Skeletons count={4} w="80%" h={8} mt={4} />;
    if (items.length === 0) return <Callout>{emptyState}</Callout>;

    const seeMore = async () => {
        if (hiddenItemsCount) {
            setHiddenItemsCount(Math.max(hiddenItemsCount - itemsPerPage, 0));
            return;
        }
        await fetchMore({ variables: { after: endCursor } });
    };

    const seeLess = async () => {
        setHiddenItemsCount(items.length - itemsPerPage);
        onSeeLess?.();
    };

    const visibleItemsCount = items.length - hiddenItemsCount;
    const visibleItems = items.slice(0, visibleItemsCount);
    const showSeeMore = Boolean(hasNextPage || hiddenItemsCount);
    const showSeeLess = visibleItemsCount > itemsPerPage;

    return (
        <>
            <VStack align="stretch" divider={<Divider />} spacing={0}>
                {visibleItems.map((item) => {
                    return renderItem(item);
                })}
                {(showSeeMore || showSeeLess) && (
                    <FlexRow py={5} color="gray.400" gap={4} {...props}>
                        {showSeeMore && (
                            <ArrowLinkText
                                arrowDirection="down"
                                onClick={seeMore}
                            >
                                See more
                            </ArrowLinkText>
                        )}

                        {showSeeLess && (
                            <ArrowLinkText
                                arrowDirection="up"
                                onClick={seeLess}
                            >
                                See less
                            </ArrowLinkText>
                        )}
                    </FlexRow>
                )}
            </VStack>
        </>
    );
};
